import { setOwner } from '@ember/application';
import { registerDestructor } from '@ember/destroyable';
import { action } from '@ember/object';
import ComputedProperty from '@ember/object/computed';
import Transition from '@ember/routing/-private/transition';
import type Route from 'cio/routes/base';
import RouterService from '@ember/routing/router-service';
import { inject as service } from '@ember/service';
import type ConfirmChangesService from 'cio/services/confirm-changes';
import EventBusService, { CONFIRM_TRANSITION } from 'cio/services/event-bus';
import isEqual from 'lodash.isequal';

export type Dirtyable = {
  isDirty?: boolean | ComputedProperty<boolean, any>;
};

const UNLOAD_MESSAGE = 'You have unsaved changes';

function destructor(instance: ConfirmChangesInstance) {
  instance.cleanup();
}

function isDifferentRoute(transition: Transition) {
  let { from, to } = transition;

  if (from == null) {
    return true;
  }

  if (from.name === to.name) {
    // We could be transitioning within the same route, but changing models, in
    // which case we want to confirm the transition.

    return !isEqual(from.params, to.params);
  }

  return true;
}

export class ConfirmChangesInstance {
  @service router!: RouterService;
  @service eventBus!: EventBusService;
  @service confirmChanges!: ConfirmChangesService;

  private dirtyable: Dirtyable;
  private dirtyableRoute?: Route;

  // If `dirtyableRoute` is passed in, we should automatically listen to the
  // deactivate hook to cleanup the listeners
  constructor(owner: unknown, dirtyable: Dirtyable, dirtyableRoute?: Route) {
    setOwner(this, owner as any);

    this.dirtyable = dirtyable;
    this.dirtyableRoute = dirtyableRoute;

    this.addListeners();

    if (dirtyableRoute != null) {
      dirtyableRoute.on('deactivate', this.cleanup);
    } else {
      registerDestructor(dirtyable, () => destructor(this));
    }
  }

  // Provide external access to check if the dirtyable object is dirty
  get isDirty() {
    return this.dirtyable.isDirty;
  }

  @action
  cleanup() {
    this.removeListeners();
    this.dirtyableRoute?.off('deactivate', this.cleanup);
    this.confirmChanges.removeManager(this);
  }

  @action
  private handleWindowClose(evt: Event) {
    if (this.isDirty) {
      evt.preventDefault();
      // Technically `returnValue` is deprecated, but still advised for this
      // specific use case!
      // https://developer.mozilla.org/en-US/docs/Web/API/Window/beforeunload_event
      return ((evt as any).returnValue = UNLOAD_MESSAGE);
    }

    return;
  }

  private isFileDragEventWhenDirty(evt: DragEvent) {
    return evt.dataTransfer?.types.includes('Files') === true && this.isDirty;
  }

  @action
  private handleDrop(evt: DragEvent) {
    if (!this.isFileDragEventWhenDirty(evt)) {
      return;
    }

    let target = evt.target as HTMLElement | null;
    let isFileInput = target?.tagName === 'INPUT' && (target as HTMLInputElement).type === 'file';

    // ensure native behavior is retained when dropping onto <input type="file"> elements
    if (!isFileInput) {
      evt.preventDefault();
    }
  }

  @action
  private handleDragOver(evt: DragEvent) {
    if (!this.isFileDragEventWhenDirty(evt)) {
      return;
    }

    // preventDefault in a "dragover" event handler is necessary for the
    // preventDefault in the "drop" event handler to work.
    // https://developer.mozilla.org/en-US/docs/Web/API/HTML_Drag_and_Drop_API/Drag_operations#specifying_drop_targets
    evt.preventDefault();
  }

  @action
  private handleRouteTransition(transition: Transition) {
    if (this.isDirty && isDifferentRoute(transition)) {
      transition.abort();
      this.eventBus.publish(CONFIRM_TRANSITION, transition);
    }
  }

  @action
  private addListeners() {
    window.addEventListener('beforeunload', this.handleWindowClose);
    window.addEventListener('dragover', this.handleDragOver);
    window.addEventListener('drop', this.handleDrop);
    this.router.on('routeWillChange', this.handleRouteTransition);
  }

  @action
  private removeListeners() {
    // @ts-ignore = Ember 3 doesn't (officially) support router.off, but Ember 4
    // does. Leave this in until we upgrade.
    this.router.off?.('routeWillChange', this.handleRouteTransition);
    window.removeEventListener('beforeunload', this.handleWindowClose);
    window.removeEventListener('dragover', this.handleDragOver);
    window.removeEventListener('drop', this.handleDrop);
  }
}
